import { useState } from "react";
import { Guitar, Piano, Search } from "lucide-react";
import { toast } from "sonner";
import { FLATS, NOTES, TYPES, chordName, chordNotes, guitarMidis, guitarShape, pianoMidis } from "../../lib/chords";
import { playNotes } from "../../lib/synth";

const GuitarDiagram = ({ frets }) => {
  const played = frets.filter((f) => f !== null && f > 0);
  const max = played.length ? Math.max(...played) : 0;
  const base = max <= 4 ? 1 : Math.min(...played);
  const minF = played.length ? Math.min(...played) : 0;
  const barre = minF > 0 ? frets.map((f, i) => (f === minF ? i : -1)).filter((i) => i >= 0) : [];
  const x = (i) => 20 + i * 24;
  const y = (row) => 40 + row * 30;
  return (
    <svg viewBox="0 0 160 200" className="w-44" data-testid="chord-guitar-diagram">
      {base === 1 ? <rect x={18} y={36} width={124} height={5} fill="#e5e7eb" /> : <text x={2} y={y(0.65)} fill="#9ca3af" fontSize="11" fontFamily="monospace">{base}fr</text>}
      {[0, 1, 2, 3, 4, 5].map((r) => <line key={r} x1={20} x2={140} y1={y(r)} y2={y(r)} stroke="#3f3f46" />)}
      {frets.map((_, i) => <line key={i} x1={x(i)} x2={x(i)} y1={y(0)} y2={y(5)} stroke="#71717a" strokeWidth={1 + (5 - i) * 0.25} />)}
      {barre.length > 1 && frets[barre[0]] === minF && (
        <rect x={x(barre[0]) - 7} y={y(minF - base + 0.5) - 7} width={x(barre[barre.length - 1]) - x(barre[0]) + 14} height={14} rx={7} fill="#facc15" />
      )}
      {frets.map((f, i) => (f === null ? (
        <text key={i} x={x(i)} y={28} textAnchor="middle" fill="#ec4899" fontSize="12" fontWeight="bold">×</text>
      ) : f === 0 ? (
        <circle key={i} cx={x(i)} cy={24} r={4.5} fill="none" stroke="#39ff6a" strokeWidth="1.5" />
      ) : (
        <circle key={i} cx={x(i)} cy={y(f - base + 0.5)} r={7} fill="#facc15" />
      )))}
    </svg>
  );
};

const WHITE = [0, 2, 4, 5, 7, 9, 11];
const PianoKeys = ({ highlight }) => {
  const keys = Array.from({ length: 25 }, (_, i) => 60 + i);
  const whites = keys.filter((m) => WHITE.includes(m % 12));
  const w = 100 / whites.length;
  const label = (m) => `${NOTES[m % 12].replace("#", "s")}${Math.floor(m / 12) - 1}`;
  return (
    <div className="relative h-32 w-full select-none" data-testid="chord-piano">
      {whites.map((m, i) => (
        <button key={m} onClick={() => playNotes([m], { duration: 1 })} data-testid={`chord-piano-key-${label(m)}`}
          className={`absolute top-0 h-full border border-zinc-900 rounded-b-[3px] transition-colors ${highlight.includes(m) ? "bg-yellow-400" : "bg-zinc-100 hover:bg-zinc-300"}`}
          style={{ left: `${i * w}%`, width: `${w}%` }} />
      ))}
      {keys.filter((m) => !WHITE.includes(m % 12)).map((m) => {
        const idx = whites.filter((x) => x < m).length;
        return (
          <button key={m} onClick={() => playNotes([m], { duration: 1 })} data-testid={`chord-piano-key-${label(m)}`}
            className={`absolute top-0 z-10 h-[60%] rounded-b-[3px] border border-black transition-colors ${highlight.includes(m) ? "bg-pink-500" : "bg-zinc-900 hover:bg-zinc-700"}`}
            style={{ left: `${idx * w - w * 0.3}%`, width: `${w * 0.6}%` }} />
        );
      })}
    </div>
  );
};

const parse = (q) => {
  const m = q.trim().match(/^([A-Ga-g])([#b]?)(.*)$/);
  if (!m) return null;
  let root = m[1].toUpperCase() + m[2];
  if (m[2] === "b") root = Object.keys(FLATS).find((k) => FLATS[k] === root) || { Cb: "B", Fb: "E" }[root];
  const suffix = m[3].trim().replace(/^min$/, "m").replace(/^major$|^maj$/, "");
  const type = TYPES.find((t) => t.suffix === suffix || t.label.toLowerCase() === suffix.toLowerCase());
  return root && NOTES.includes(root) && type ? { root, type: type.id } : null;
};

export const ChordDictionary = ({ compact = false }) => {
  const [root, setRoot] = useState("C");
  const [type, setType] = useState("maj");
  const [q, setQ] = useState("");
  const frets = guitarShape(root, type);
  const pm = pianoMidis(root, type);
  const search = (e) => {
    e.preventDefault();
    const r = parse(q);
    if (!r) return toast.error("Try a chord like Am7, F#m or Bbmaj7");
    setRoot(r.root);
    setType(r.type);
  };
  const pill = (active) => `mono text-xs rounded-[3px] border px-2.5 py-1.5 transition-colors ${active ? "border-yellow-400 bg-yellow-400 text-black font-bold" : "border-zinc-800 text-zinc-300 hover:border-yellow-400/60"}`;
  return (
    <div className="space-y-5" data-testid="chord-dictionary">
      <form onSubmit={search} className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search a chord, e.g. Am7" data-testid="chord-search-input"
            className="w-full rounded-[3px] bg-black border border-zinc-800 pl-9 pr-3 py-2 text-sm outline-none focus:border-yellow-400" />
        </div>
        <button className="rounded-[3px] bg-yellow-400 px-4 text-sm font-bold text-black hover:bg-yellow-300" data-testid="chord-search-btn">Find</button>
      </form>
      <div className="flex flex-wrap gap-1.5">
        {NOTES.map((n) => <button key={n} onClick={() => setRoot(n)} className={pill(root === n)} data-testid={`chord-key-${n.replace("#", "s")}`}>{n}{FLATS[n] ? `/${FLATS[n]}` : ""}</button>)}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {TYPES.map((t) => <button key={t.id} onClick={() => setType(t.id)} className={pill(type === t.id)} data-testid={`chord-type-${t.id}`}>{t.label}</button>)}
      </div>
      <div className={`grid gap-6 ${compact ? "" : "md:grid-cols-[auto_1fr]"} items-start`}>
        <div className="panel p-5 flex flex-col items-center">
          <span className="text-4xl font-black text-yellow-400" data-testid="chord-name">{chordName(root, type)}</span>
          <span className="mono text-xs text-zinc-400 mt-1" data-testid="chord-notes">{chordNotes(root, type).join(" · ")}</span>
          <GuitarDiagram frets={frets} />
          <button onClick={() => playNotes(guitarMidis(frets), { strum: 0.045, duration: 2.2 })} data-testid="chord-play-guitar-btn"
            className="mt-2 flex items-center gap-2 rounded-[3px] border border-zinc-700 px-3 py-1.5 text-sm hover:border-[#39ff6a] hover:text-[#39ff6a] transition-colors">
            <Guitar className="h-4 w-4" /> Strum it
          </button>
        </div>
        <div className="panel p-5 space-y-4">
          <span className="mono-label">Piano voicing</span>
          <PianoKeys highlight={pm} />
          <button onClick={() => playNotes(pm, { duration: 2, wave: "sine" })} data-testid="chord-play-piano-btn"
            className="flex items-center gap-2 rounded-[3px] border border-zinc-700 px-3 py-1.5 text-sm hover:border-[#39ff6a] hover:text-[#39ff6a] transition-colors">
            <Piano className="h-4 w-4" /> Play on piano
          </button>
        </div>
      </div>
    </div>
  );
};
